import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLayout } from "../../hooks/api/useLayout";
import type { Reservation } from "../../hooks/api/useReservations";
import { useReservations } from "../../hooks/api/useReservations";
import { type Seat, useSeats } from "../../hooks/api/useSeats";
import { useMapTransform } from "../../hooks/useMapTransform";
import { Alert } from "../ui/Alert";
import { SeatMapSkeleton } from "../ui/Skeleton";
import { SeatLegend } from "./SeatLegend";
import {
  type SeatStatus,
  seatStatusStyles,
  seatStatusTextStyles,
} from "./seatColors";
import { DEFAULT_IMAGE_HEIGHT, DEFAULT_Y_OFFSET } from "./seatPositions";

/** マップ下部の余白（Tailwind space-6 = 24px相当） */
const VIEWPORT_BOTTOM_MARGIN_PX = 24;

/** マップコンテナの最小幅（px） */
const MIN_CONTAINER_WIDTH_PX = 320;

/** ツー��チップ表示までの遅延（ms）— チラつき防止 */
const TOOLTIP_DELAY_MS = 150;

/** ツールチップのY方向オフセット（px）— カーソルの上に表示 */
const TOOLTIP_OFFSET_Y_PX = 32;

/** シングルクリック遅延（ms）— ダブルクリックとの競合回避 */
const CLICK_DELAY_MS = 250;

/** 座席タイル内テキストの基準フォントサイズ（px）— scale=1 時の値 */
const BASE_FONT_SIZE_PX = 10;

interface SeatMapProps {
  date: string;
  onSeatClick?: (seatId: number, reservation?: Reservation) => void;
  onSeatDoubleClick?: (seatId: number, reservation: Reservation) => void;
}

function getSeatStatus(reservation?: Reservation): SeatStatus {
  if (!reservation) {
    return "available";
  }
  return reservation.isOwn ? "own" : "occupied";
}

export function SeatMap({
  date,
  onSeatClick,
  onSeatDoubleClick,
}: SeatMapProps) {
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const {
    containerRef,
    containerEl,
    transform,
    isZoomed,
    resetZoom,
    handlers,
  } = useMapTransform();
  const [tooltip, setTooltip] = useState<{
    name: string;
    x: number;
    y: number;
  } | null>(null);
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // レイアウト情報を取得
  const {
    data: layout,
    isLoading: layoutLoading,
    error: layoutError,
  } = useLayout();

  // 座席データをAPIから取得
  const {
    data: seats,
    isLoading: seatsLoading,
    error: seatsError,
  } = useSeats();

  const {
    data: reservations,
    isLoading: reservationsLoading,
    error: reservationsError,
  } = useReservations(date);

  // レイアウト情報から計算
  const imageUrl = layout?.imageUrl ?? "";
  const imageHeight = layout?.originalHeight ?? DEFAULT_IMAGE_HEIGHT;
  const yOffset = layout?.yOffset ?? DEFAULT_Y_OFFSET;
  const aspectRatio = layout?.aspectRatio ?? 1;

  // ビューポートに収まる最大幅を計算
  const [containerMaxWidth, setContainerMaxWidth] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    if (!(aspectRatio && imageLoaded && containerEl)) {
      return;
    }
    const update = () => {
      const containerTop = containerEl.getBoundingClientRect().top;
      const availableHeight = Math.max(
        window.innerHeight - containerTop - VIEWPORT_BOTTOM_MARGIN_PX,
        0
      );
      const maxWidthFromHeight = Math.max(
        availableHeight * aspectRatio,
        MIN_CONTAINER_WIDTH_PX
      );
      setContainerMaxWidth(`${Math.floor(maxWidthFromHeight)}px`);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [aspectRatio, imageLoaded, containerEl]);

  const reservationMap = useMemo(() => {
    const map = new Map<number, Reservation>();
    if (reservations) {
      for (const reservation of reservations) {
        map.set(reservation.seatId, reservation);
      }
    }
    return map;
  }, [reservations]);

  const maxChars = Math.floor(Math.max(transform.scale, 1));
  const seatFontSize = `${BASE_FONT_SIZE_PX / maxChars}px`;

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const clearTooltipTimer = useCallback(() => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
      tooltipTimerRef.current = null;
    }
  }, []);

  const handleSeatPointerEnter = useCallback(
    (e: React.PointerEvent, userName: string) => {
      // パン操作中（ドラッグ中）はツールチップを表示しない
      if (e.buttons !== 0) {
        return;
      }
      clearTooltipTimer();
      const rect = containerEl?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      tooltipTimerRef.current = setTimeout(() => {
        setTooltip({
          name: userName,
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }, TOOLTIP_DELAY_MS);
    },
    [clearTooltipTimer, containerEl]
  );

  const handleSeatPointerLeave = useCallback(() => {
    clearTooltipTimer();
    setTooltip(null);
  }, [clearTooltipTimer]);

  useEffect(() => {
    return () => {
      clearTooltipTimer();
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
      }
    };
  }, [clearTooltipTimer]);

  const handleSeatClick = (seat: Seat) => {
    const reservation = reservationMap.get(seat.seatId);
    // ダブルクリック判定のためシングルクリックを遅延実行し、モーダルのチラつきを防止
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      if (onSeatClick) {
        onSeatClick(seat.seatId, reservation);
      }
    }, CLICK_DELAY_MS);
  };

  const handleSeatDoubleClick = (e: React.MouseEvent, seat: Seat) => {
    e.stopPropagation();
    // シングルクリックのタイマーをキャンセルしてモーダル表示を抑止
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    const reservation = reservationMap.get(seat.seatId);
    if (reservation?.isOwn && onSeatDoubleClick) {
      onSeatDoubleClick(seat.seatId, reservation);
    }
  };

  if (layoutLoading || seatsLoading || reservationsLoading) {
    return <SeatMapSkeleton />;
  }

  if (layoutError) {
    return (
      <Alert variant="warning">
        見取り図が設定されていません。管理者に連絡してください。
      </Alert>
    );
  }

  if (seatsError || reservationsError) {
    return (
      <Alert variant="error">
        座席情報の取得に失敗しました。ページを再読み込みしてください。
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <SeatLegend />

      <div
        ref={containerRef}
        className={`relative max-w-full mx-auto overflow-hidden touch-none ${isZoomed ? "cursor-grab" : ""}`}
        style={{
          aspectRatio: `${aspectRatio}`,
          width: containerMaxWidth ?? "100%",
        }}
        role="region"
        aria-label="座席マップ"
        data-testid="seat-map"
        {...handlers}
        onMouseLeave={() => {
          handlers.onMouseLeave();
          handleSeatPointerLeave();
        }}
      >
        <div
          className="absolute inset-0 origin-center"
          style={{
            transform: `scale(${transform.scale}) translate(${transform.translateX}%, ${transform.translateY}%)`,
          }}
        >
          {/* 背景画像 */}
          <img
            src={imageUrl}
            alt="オフィスレイアウト"
            className="absolute inset-0 w-full h-full object-cover object-bottom"
            onLoad={handleImageLoad}
            style={{
              objectPosition: `center ${(yOffset / imageHeight) * 100}%`,
            }}
          />

          {/* 座席オーバーレイ（相対座標で配置） */}
          {imageLoaded &&
            seats?.map((seat) => {
              const reservation = reservationMap.get(seat.seatId);
              const status = getSeatStatus(reservation);

              return (
                <button
                  key={seat.seatId}
                  type="button"
                  onClick={() => handleSeatClick(seat)}
                  onDoubleClick={(e) => handleSeatDoubleClick(e, seat)}
                  data-testid={`seat-${seat.seatId}`}
                  aria-label={getAriaLabel(seat, reservation, status)}
                  onPointerEnter={
                    reservation && !reservation.isOwn
                      ? (e) => handleSeatPointerEnter(e, reservation.userName)
                      : undefined
                  }
                  onPointerLeave={
                    reservation && !reservation.isOwn
                      ? handleSeatPointerLeave
                      : undefined
                  }
                  className={`
                    absolute border-2 rounded
                    flex flex-col items-center justify-center
                    font-bold leading-tight overflow-hidden
                    transition-all duration-150 cursor-pointer
                    ${seatStatusStyles[status]}
                    ${seatStatusTextStyles[status]}
                  `}
                  style={{
                    left: `${seat.xPercent ?? 0}%`,
                    top: `${seat.yPercent ?? 0}%`,
                    width: `${seat.widthPercent ?? 5}%`,
                    height: `${seat.heightPercent ?? 5}%`,
                    fontSize: seatFontSize,
                  }}
                >
                  <span className="block text-center whitespace-nowrap">
                    {/* 「空」は常に1文字のため切り詰め不要 */}
                    {reservation
                      ? reservation.userName.slice(0, maxChars)
                      : "空"}
                  </span>
                </button>
              );
            })}
        </div>

        {/* カスタムツールチップ */}
        {tooltip && (
          <div
            role="tooltip"
            className="pointer-events-none absolute z-20 rounded bg-gray-800 px-2 py-1 text-xs text-white shadow-lg whitespace-nowrap"
            style={{
              left: `${tooltip.x}px`,
              top: `${Math.max(4, tooltip.y - TOOLTIP_OFFSET_Y_PX)}px`,
            }}
          >
            {tooltip.name}
          </div>
        )}

        {/* ズームリセットボタン */}
        {isZoomed && (
          <button
            type="button"
            onClick={resetZoom}
            className="absolute top-2 right-2 z-10 rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-gray-700 shadow-md hover:bg-white"
            aria-label="ズームをリセット"
          >
            {Math.round(transform.scale * 100)}%{" "}
            <span aria-hidden="true">✕</span>
          </button>
        )}
      </div>

      {/* ズーム操作ヒント */}
      <p className="text-xs text-gray-400 text-center">
        ホイールまたはピンチでズーム・ダブルクリックでズームリセット・自分の座席をダブルクリックで予約キャンセル
      </p>
    </div>
  );
}

function getAriaLabel(
  seat: Seat,
  reservation: Reservation | undefined,
  status: SeatStatus
): string {
  const base = `座席 ${seat.seatId}${seat.area ? `（${seat.area}）` : ""}`;
  switch (status) {
    case "available":
      return `${base} - 空席、クリックして予約`;
    case "own":
      return `${base} - あなたの予約、クリックしてキャンセル`;
    case "occupied":
      return `${base} - ${reservation?.userName}が予約中、クリックして詳細表示`;
    default:
      return base;
  }
}
